import { ContextStore, ExperimentStore } from 'bernie-plugin-mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';

import { Localization } from 'bernie-l10n';

import { ILocalization } from '@shared-ui/localization-context';
import { PropertyListingHeaderMessages } from '@shared-ui/lodging-property-listing';
import { SortAndFiltersViewType } from '@shared-ui/retail-sort-and-filter-lodging';
import { useLodgingURLParamsToQueryInputs } from '@shared-ui/search-tools-lodging';
import { ViewLarge, Viewport, ViewSmall } from '@shared-ui/viewport-context';

import { getPropertySearchInput } from 'stores/adapters';
import { hasStoresChanged } from 'stores/utils';
import { MultiItemStore } from 'stores/multi-item-store';
import { SearchCriteriaModel } from 'stores/models';
import { SearchQueryAdapter } from 'stores/adapters/search-query-adapter';
import { SearchResultsStore } from 'stores/search-results-store';

import { LodgingEGDSPlainTextFragment, LodgingMoreInfoTriggerFragment, LodgingPwaPropertySearch } from '__generated__/api/types';
import { LoyaltyMessagingModel } from 'models';

import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkText } from 'uitk-react-text';

import { ResultsSubHeaderWrapper } from 'components/search/results-sub-header-wrapper';
import { SWPToggle } from 'components/common/swp-toggle';
import { UniversalSortFilterWrapper } from './sort-filter/universal-sort-filter';

export interface ResultsSubHeaderProps {
  resultMessages?: (LodgingPwaPropertySearch.resultMessages &
    LodgingEGDSPlainTextFragment.fragment &
    LodgingMoreInfoTriggerFragment.fragment)[];
  isLoading: boolean;
  l10n: ILocalization;
  loyaltyMessagingModel?: LoyaltyMessagingModel;
  searchCriteria?: SearchCriteriaModel;
  searchResults?: SearchResultsStore;
  isQuickAccessFiltersActive: boolean;
  multiItem?: MultiItemStore;
  experiment?: ExperimentStore;
  context?: ContextStore;
}

export const ResultsSubHeader: React.FunctionComponent<ResultsSubHeaderProps> = inject(
  'context',
  'searchCriteria',
  'searchResults',
  'experiment',
  'multiItem'
)(
  // TODO: Cleanup this eslint disable when we refactor to remove the shopping-pwa implementation of this subheader
  // eslint-disable-next-line complexity
  observer(props => {
    const {
      resultMessages,
      isLoading,
      l10n,
      loyaltyMessagingModel,
      searchCriteria,
      searchResults,
      isQuickAccessFiltersActive,
      context,
      experiment,
      multiItem,
    } = props;
    const listingsQueryInputs = useLodgingURLParamsToQueryInputs();
    const isError = searchResults?.isError;
    const multiItemId = searchCriteria && searchCriteria.misId;
    const lob = multiItemId ? 'multiItem' : 'hotels';
    const localizedText = l10n.formatText(`${lob}.searchResults.loadingMessage.text`);

    if (!context || !searchCriteria || !searchResults) {
      return null;
    }

    const useSharedUIListingsQuery =
      !multiItem?.isPackageShoppingPath() && experiment?.exposures?.EG_Property_Search_Query_Migration?.bucket === 1;

    const searchInputs = useSharedUIListingsQuery ? getPropertySearchInput(context, searchCriteria) : undefined;
    const rooms = listingsQueryInputs?.propertySearchCriteriaInput?.primary.rooms;
    if (searchInputs?.criteria?.primary && rooms) {
      searchInputs.criteria.primary.rooms = rooms;
    }

    const modelData = SearchQueryAdapter.parseIn(searchCriteria.toJSON());
    const incomingSearchCriteria = new SearchCriteriaModel(modelData, new Localization(context.locale));
    const criteriaChanged = useSharedUIListingsQuery ? hasStoresChanged(searchResults.searchCriteria, incomingSearchCriteria) : undefined;

    return (
      <header>
        <Viewport>
          <ViewSmall>
            <>
              {!isError && (
                <>
                  {loyaltyMessagingModel && <SWPToggle model={loyaltyMessagingModel} searchCriteria={searchCriteria} />}
                  {useSharedUIListingsQuery ? (
                    <ResultsSubHeaderWrapper searchInputs={searchInputs} localizedText={localizedText} criteriaChanged={criteriaChanged} />
                  ) : (
                    <HeaderMessages loading={isLoading} localizedText={localizedText} resultMessages={resultMessages} />
                  )}
                </>
              )}
            </>
          </ViewSmall>
          <ViewLarge>
            <UitkLayoutGrid columns={8}>
              <UitkLayoutGridItem colSpan={5}>
                <div>
                  {!isError && (
                    <>
                      {loyaltyMessagingModel && <SWPToggle model={loyaltyMessagingModel} searchCriteria={searchCriteria} />}
                      {useSharedUIListingsQuery ? (
                        <ResultsSubHeaderWrapper
                          searchInputs={searchInputs}
                          localizedText={localizedText}
                          criteriaChanged={criteriaChanged}
                        />
                      ) : (
                        <HeaderMessages loading={isLoading} localizedText={localizedText} resultMessages={resultMessages} />
                      )}
                    </>
                  )}
                </div>
              </UitkLayoutGridItem>
              {!isQuickAccessFiltersActive && (
                <UitkLayoutGridItem colSpan={3}>
                  <div>
                    <UniversalSortFilterWrapper view={SortAndFiltersViewType.SORT} />
                  </div>
                </UitkLayoutGridItem>
              )}
            </UitkLayoutGrid>
          </ViewLarge>
        </Viewport>
      </header>
    );
  })
);
ResultsSubHeader.displayName = 'ResultsSubHeader';

export interface HeaderMessagesProps {
  loading: boolean;
  localizedText: string;
  resultMessages?: (LodgingPwaPropertySearch.resultMessages &
    LodgingEGDSPlainTextFragment.fragment &
    LodgingMoreInfoTriggerFragment.fragment)[];
}

export const HeaderMessages: React.FunctionComponent<HeaderMessagesProps> = ({ loading, localizedText, resultMessages }) => {
  if (loading) {
    return (
      <UitkSpacing padding={{ blockend: 'two' }}>
        <UitkText inline={false} align="left" theme="default">
          {localizedText}
        </UitkText>
      </UitkSpacing>
    );
  }

  if (!resultMessages || resultMessages.length === 0) {
    return null;
  }

  return (
    <div data-stid="results-header-messages">
      <PropertyListingHeaderMessages messages={resultMessages} />
    </div>
  );
};
HeaderMessages.displayName = 'HeaderMessages';
